import {
  type CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  type InputSignal,
  output,
  type OutputEmitterRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, switchMap } from 'rxjs';

import type { TermAndCondition } from '@core/models/app.model';
import { NotifyService } from '@core/services/notifyService.service';
import { downloadFileAction } from '@helpers/download-file';
import { DIALOG_SIZE } from '@shared/constants/ume-constants';
import { ConfirmationDialogService } from '@shared/dialogs/confirmation-dialog/confirmation-dialog.service';
import type { IDownloadedFileResponse } from '@shared/files/download-file.service';

import { ProviderService } from '../../../../../provider/services/provider.service';
import { TermHistoryDialogComponent } from '../../../dialogs/history-modal/term-history-dialog.component';
import { UploadTermDialogComponent } from '../../../dialogs/upload-term-modal/upload-term-dialog.component';
import type { IUploadTermData } from '../../../models/services.model';

@UntilDestroy()
@Component({
  selector: 'ume-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  styleUrls: ['./terms-and-conditions.component.scss'],
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DragDropModule,
    MatMenuModule,
  ],
})
export class TermsAndConditionsComponent {
  private readonly confirmationService: ConfirmationDialogService =
    inject(ConfirmationDialogService);
  private readonly notify: NotifyService = inject(NotifyService);
  private readonly providerService: ProviderService = inject(ProviderService);
  public readonly dialog: MatDialog = inject(MatDialog);

  public offeringId: InputSignal<string | null> = input.required();
  public linkedTermsAndConditions: InputSignal<TermAndCondition[]> = input.required();
  public notLinkedTermsAndConditions: InputSignal<TermAndCondition[]> = input.required();

  public refresh: OutputEmitterRef<void> = output();

  public drop(event: CdkDragDrop<TermAndCondition[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if (event.container.id === 'linkedList') {
        this.linkTerm(event.previousContainer.data[event.previousIndex], true);
      } else {
        this.linkTerm(event.previousContainer.data[event.previousIndex], false);
      }
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  public createTerm(): void {
    this.dialog
      .open(UploadTermDialogComponent, {
        data: {
          serviceOfferingId: this.offeringId(),
        },
        width: DIALOG_SIZE.MODAL_SM_UP,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data: { uploadData: IUploadTermData; file: File }) =>
          this.providerService.createNewTerm(data.file, data.uploadData)
        ),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.refresh.emit();
        this.notify.notifySuccess('New term has been added successfully');
      });
  }

  public showHistory(term: TermAndCondition): void {
    this.dialog
      .open(TermHistoryDialogComponent, {
        data: {
          term: term,
        },
        width: DIALOG_SIZE.MODAL_SM,
      })
      .afterClosed()
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => {});
  }

  public updateTerm(term: TermAndCondition): void {
    this.dialog
      .open(UploadTermDialogComponent, {
        data: {
          serviceOfferingId: this.offeringId(),
          term,
        },
        width: DIALOG_SIZE.MODAL_SM_UP,
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data: { uploadData: IUploadTermData; file?: File }) => {
          return this.providerService.updateTerm(
            data.uploadData,
            data.uploadData.termsAndConditions.guid,
            data.file
          );
        }),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.refresh.emit();
        this.notify.notifySuccess('Term has been updated successfully');
      });
  }

  public deleteTerm(term: TermAndCondition): void {
    this.confirmationService
      .confirm({
        title: 'Are you sure you want to delete this term?',
        text: `
            <p class="text">If you delete this term, you will not be able to restore it.</p>
          `,
        width: DIALOG_SIZE.MODAL_SM,
      })
      .pipe(
        filter(Boolean),
        switchMap(() => this.providerService.deleteTermAndCondition(term.guid)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.refresh.emit();
        this.notify.notifySuccess('This term has been deleted successfully.');
      });
  }

  public downloadTerm(term: TermAndCondition): void {
    this.providerService
      .downloadTermFile(term.guid)
      .pipe(untilDestroyed(this))
      .subscribe((result: IDownloadedFileResponse) => {
        downloadFileAction(result.body, result.contentDisposition);
      });
  }

  public linkTerm(term: TermAndCondition, link: boolean): void {
    const action = link ? 'linkTermAndCondition' : 'unlinkTermAndCondition';
    const message = link
      ? 'Term has been linked successfully'
      : 'This term has been unlinked successfully.';

    this.providerService[action](term.guid, this.offeringId() ?? '')
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.refresh.emit();
        this.notify.notifySuccess(message);
      });
  }
}
